import { API_BASE } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { Card } from "@/components/ui/card";
import { Clock, User, Users, Radio } from "lucide-react";
import { liveStyle } from "@/lib/liveTypes";
import { hora12 } from "@/lib/dateFormat";

/** Portada de la sesión: imagen de material, portada del recurso adjunto o degradado del tipo. */
const coverUrl = (s) => {
  const img = (s.materials || []).find((m) => m.type === "image");
  if (img) return `${API_BASE}/api/files/${img.file_id}/view`;
  const res = (s.materials || []).find(
    (m) => m.type === "resource" && (m.cover_url || m.resource_kind === "image"),
  );
  if (res) return res.cover_url || `${API_BASE}/api/resources/${res.resource_id}/image`;
  return null;
};

/** Tarjeta de sesión en vivo con el mismo lenguaje visual que Recursos:
 *  portada por tipo de actividad, fecha y hora destacadas, tema, profesor y
 *  cupos, con la acción principal abajo. */
export const LiveSessionCard = ({ session: s, locale = "es-CR", onOpen, actions, badges }) => {
  const { t, lang } = useI18n();
  const st = liveStyle(s.activity_type);
  const img = coverUrl(s);
  const d = new Date(`${s.date}T12:00:00`);
  const weekday = d.toLocaleDateString(locale, { weekday: "short" });
  const month = d.toLocaleDateString(locale, { month: "short" });

  return (
    <Card
      data-testid={`live-card-${s.id}`}
      className="group overflow-hidden border-slate-200 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 flex flex-col"
    >
      <button
        type="button"
        onClick={onOpen}
        disabled={!onOpen}
        data-testid={`live-card-cover-${s.id}`}
        className="relative block h-36 w-full overflow-hidden text-left"
      >
        {img ? (
          <img src={img} alt={s.title}
            className={`h-full w-full object-cover transition-transform duration-300 group-hover:scale-105 ${s.is_past ? "grayscale opacity-80" : ""}`} />
        ) : (
          <div className={`h-full w-full bg-gradient-to-br ${st.cover} ${s.is_past ? "grayscale opacity-80" : ""} flex items-center justify-center`}>
            <Radio className="h-9 w-9 text-white/80" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/25" />
        <div className="absolute top-3 left-3 rounded-xl bg-white/95 backdrop-blur px-2.5 py-1 text-center shadow-sm">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 leading-none">{weekday}</div>
          <div className="font-display text-xl font-bold text-slate-900 leading-none mt-0.5">{d.getDate()}</div>
          <div className="text-[10px] uppercase text-slate-500 leading-none mt-0.5">{month}</div>
        </div>
        <div className="absolute top-3 right-3 flex flex-wrap justify-end gap-1.5 max-w-[65%]">
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/95 ${st.text}`}>
            {t(`live_type_${s.activity_type}`) === `live_type_${s.activity_type}` ? t("live_type_other") : t(`live_type_${s.activity_type}`)}
          </span>
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/85 text-slate-700">
            {(s.levels || []).length ? s.levels.join(" · ") : t("live_all_levels")}
          </span>
          {badges}
        </div>
        <div className="absolute inset-x-0 bottom-0 px-4 pb-3">
          <div className="inline-flex items-center gap-1.5 text-white/90 text-xs font-semibold mb-1">
            <Clock className="h-3.5 w-3.5" /> {hora12(s.start_time, lang)} – {hora12(s.end_time, lang)}
          </div>
          <div className="text-white font-bold text-base leading-tight drop-shadow line-clamp-2">{s.title}</div>
        </div>
      </button>

      <div className="p-4 flex-1 flex flex-col gap-3">
        {s.description && <p className="text-sm text-slate-600 line-clamp-2">{s.description}</p>}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> {s.teacher_name}</span>
          <span className="inline-flex items-center gap-1.5" data-testid={`live-card-seats-${s.id}`}>
            <Users className="h-3.5 w-3.5" /> {s.booked_count}/{s.capacity}
            {s.seats_left !== undefined ? ` · ${t("live_seats_left")}: ${s.seats_left}` : ""}
          </span>
        </div>
        {actions && <div className="mt-auto pt-1">{actions}</div>}
      </div>
    </Card>
  );
};

export default LiveSessionCard;
